import {
  Component,
  OnInit,
  HostBinding,
  HostListener,
  ViewChild,
  ElementRef,
  Input,
  ComponentFactoryResolver,
} from '@angular/core';
import { EventService } from './event.service';
import { AdDirective } from './ad.directive';
import { ComponentProperties, OverlayProperties } from './interfaces';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'overlay',
  templateUrl: './overlay-container.component.html',
  styleUrls: ['./overlay-container.component.sass'],
})
export class OverlayContainerComponent implements OnInit {
  public isInitial = false;
  globalEventsSubscription: any;
  public overlayBackdrop = true;
  minTimeout = 30;
  // tslint:disable-next-line:variable-name
  _hostOpacity = 0;

  @ViewChild(AdDirective, { static: true }) adHost!: AdDirective;

  @Input() properties!: OverlayProperties;

  @HostBinding('style.opacity')
  get hostOpacity() {
    return this.properties.fadeIn ? this._hostOpacity : 1;
  }

  set hostOpacity(value) {
    this._hostOpacity = value;
  }

  @HostBinding('style.transition')
  get hostTransition() {
    const duration = this.properties.animationDuration + 'ms ';
    const esing = this.properties.animationTimingFunction;
    return 'opacity ' + duration + esing + ', transform ' + duration + esing;
  }

  @HostBinding('style.transform') hostTransform!: string;

  @HostBinding('style.z-index')
  get hostZIndex() {
    return this.properties.zIndex;
  }

  @HostBinding('style.width')
  get hostWidth() {
    return this.properties.wrapperWidth;
  }

  @HostBinding('style.height')
  get hostHeight() {
    return this.properties.wrapperHeight;
  }

  @HostBinding('style.position')
  get hostPosition() {
    return this.properties.position;
  }

  @HostBinding('class.overlay-shown') hostShown = false;

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.hostShown) {
        this.closeOverlay();
      }
    }
  }

  @HostListener('transitionend', ['$event'])
  transitionEnd(event: any) {
    // tslint:disable-next-line:triple-equals
    if (event.target.tagName.toLowerCase() != 'overlay') {
      return;
    }

    // tslint:disable-next-line:triple-equals
    if (event.propertyName != 'opacity') {
      return;
    }

    if (this.hostShown) {
      this.closeOverlayEnd();
    } else {
      this.hostShown = true;
      this.eventService.emitChangeEvent({
        type: 'Shown',
      });
    }
  }

  get popoverClass() {
    if (this.properties.metadata) {
      return this.properties.metadata.popoverClass;
    }
  }

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    public elementRef: ElementRef,
    private eventService: EventService
  ) {
    this.globalEventsSubscription = this.eventService.emitter.subscribe(
      event => {
        this.handleGlobalEvents(event);
      }
    );
  }

  ngOnInit() {
    this.loadComponent();
    this.initialOverlay();

    setTimeout(() => {
      this.isInitial = true;
    }, this.minTimeout); // Note: not good

    this.eventService.emitChangeEvent({
      type: 'Show',
    });
  }

  // tslint:disable-next-line:use-lifecycle-interface
  ngAfterViewInit() {
    setTimeout(() => {
      this.showOverlayAnimation();
    }, this.minTimeout);
  }

  initialOverlay() {
    this.hostTransform =
      'translateY(' + this.properties.animationTranslateY + ')';
  }

  showOverlayAnimation() {
    this.hostOpacity = 1;
    this.hostTransform = 'translateY(0px)';
  }

  loadComponent() {
    const adItem = this.properties;
    const componentFactory =
      this.componentFactoryResolver.resolveComponentFactory(
        adItem.mainComponent
      );
    const viewContainerRef = this.adHost.viewContainerRef;

    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent(componentFactory);
    const componentRefElement = (componentRef.location as ComponentProperties)
      .nativeElement;

    // Set styles
    componentRefElement.style.width = this.properties.width;
    componentRefElement.style.height = this.properties.height;
    componentRefElement.style.maxWidth = this.properties.maxWidth;
    componentRefElement.style.minWidth = this.properties.minWidth;

    // Set classes
    if (typeof this.popoverClass === 'string') {
      componentRefElement.classList.add(this.popoverClass);
    }
    if (typeof this.popoverClass === 'object') {
      // tslint:disable-next-line:only-arrow-functions
      this.popoverClass.forEach(function (className: any) {
        componentRefElement.classList.add(className);
      });
    }

    // Pass properties
    (componentRef.instance as ComponentProperties).overlayProperties =
      this.properties;
  }

  closeOverlay() {
    this.hostOpacity = 0;
    this.hostTransform =
      'translateY(' + this.properties.animationTranslateY + ')';
  }

  closeOverlayEnd() {
    this.hostShown = false;
    this.eventService.emitChangeEvent({
      type: '[Overlay] Hide',
    });
  }

  handleGlobalEvents(event: any) {
    if (event.type === 'Hide') {
      this.closeOverlay();
    }
  }
}
